class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
    
    // Initialize mobile facets menu drawer
    const mobileMenuDrawer = document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer');
    if (mobileMenuDrawer) {
      FacetFiltersForm.initMobileFacetsMenuDrawer(mobileMenuDrawer);
    }
    
    // Initialize mobile sort select
    FacetFiltersForm.initMobileSortSelect();
  }

  static initMobileFacetsMenuDrawer(menuDrawer) {
    if (!menuDrawer) return;
    
    // Remove existing focusout listener
    menuDrawer.removeEventListener('focusout', menuDrawer.onFocusOut);
    
    // Add custom focusout handler for mobile facets
    menuDrawer.onFocusOut = function(event) {
      setTimeout(() => {
        if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) {
          // Use our custom close method for mobile facets
          FacetFiltersForm.closeMobileFacetsDrawer(this);
        }
      });
    };
    
    menuDrawer.addEventListener('focusout', menuDrawer.onFocusOut);
    
    // Add click outside handler
    FacetFiltersForm.initMobileFacetsClickOutside(menuDrawer);
  }

  static initMobileFacetsClickOutside(menuDrawer) {
    // Remove existing click listener if any
    if (menuDrawer.clickOutsideHandler) {
      document.removeEventListener('click', menuDrawer.clickOutsideHandler);
    }
    
    // Create click outside handler
    menuDrawer.clickOutsideHandler = function(event) {
      const detailsElement = menuDrawer.querySelector('details');
      if (detailsElement && detailsElement.hasAttribute('open')) {
        // Check if click is outside the mobile facets content
        const mobileInner = menuDrawer.querySelector('.mobile-facets__inner');
        const mobileOpen = menuDrawer.querySelector('.mobile-facets__open');
        
        if (mobileInner && !mobileInner.contains(event.target) && !mobileOpen.contains(event.target)) {
          FacetFiltersForm.closeMobileFacetsDrawer(menuDrawer);
        }
      }
    };
    
    // Add click listener when menu opens
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          const detailsElement = mutation.target;
          if (detailsElement.hasAttribute('open')) {
            // Menu opened - add click listener
            setTimeout(() => {
              document.addEventListener('click', menuDrawer.clickOutsideHandler);
            }, 100); // Small delay to prevent immediate closure
          } else {
            // Menu closed - remove click listener
            document.removeEventListener('click', menuDrawer.clickOutsideHandler);
          }
        }
      });
    });
    
    const detailsElement = menuDrawer.querySelector('details');
    if (detailsElement) {
      observer.observe(detailsElement, { attributes: true });
    }
  }

  static initMobileSortSelect() {
    const mobileSortSelect = document.getElementById('SortBy-mobile');
    if (mobileSortSelect) {
      // Remove existing listener to prevent duplicates
      mobileSortSelect.removeEventListener('change', FacetFiltersForm.handleMobileSortChange);
      // Add new listener
      mobileSortSelect.addEventListener('change', FacetFiltersForm.handleMobileSortChange);
    }
    
    // Initialize mobile facets close button
    FacetFiltersForm.initMobileFacetsCloseButton();
  }

  static initMobileFacetsCloseButton() {
    const mobileCloseButton = document.querySelector('.mobile-facets__close');
    if (mobileCloseButton) {
      // Remove existing listeners to prevent duplicates
      mobileCloseButton.removeEventListener('click', FacetFiltersForm.handleMobileCloseClick);
      mobileCloseButton.removeEventListener('keydown', FacetFiltersForm.handleMobileCloseKeydown);
      
      // Add new listeners
      mobileCloseButton.addEventListener('click', FacetFiltersForm.handleMobileCloseClick);
      mobileCloseButton.addEventListener('keydown', FacetFiltersForm.handleMobileCloseKeydown);
    }
  }

  static handleMobileCloseClick(event) {
    // Prevent event from bubbling up to summary
    event.preventDefault();
    event.stopPropagation();
    
    FacetFiltersForm.closeMobileFacetsDrawer(event.target);
  }

  static handleMobileCloseKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      
      FacetFiltersForm.closeMobileFacetsDrawer(event.target);
    }
  }

  static closeMobileFacetsDrawer(target) {
    // If target is already a menu-drawer, use it directly, otherwise find it
    const menuDrawer = target.tagName === 'MENU-DRAWER' ? target : target.closest('menu-drawer');
    
    if (menuDrawer) {
      const detailsElement = menuDrawer.querySelector('details');
      if (detailsElement && detailsElement.hasAttribute('open')) {
        detailsElement.removeAttribute('open');
        detailsElement.classList.remove('menu-opening');
        
        // Get breakpoint from menu-drawer data attribute
        const breakpoint = menuDrawer.dataset.breakpoint || 'mobile';
        document.body.classList.remove(`overflow-hidden-${breakpoint}`);
        
        // Remove trap focus
        const summaryElement = detailsElement.querySelector('summary');
        if (summaryElement) {
          removeTrapFocus(summaryElement);
        }
        
        // Set aria-expanded to false for accessibility
        if (summaryElement) {
          summaryElement.setAttribute('aria-expanded', false);
        }
      }
    }
  }

  static handleMobileSortChange(event) {
    const sortValue = event.target.value;
    if (sortValue) {
      // Create search params with sort_by parameter
      const currentUrl = new URL(window.location);
      const searchParams = new URLSearchParams(currentUrl.search);
      
      // Update sort_by parameter
      searchParams.set('sort_by', sortValue);
      
      // Trigger the facet filter form submission
      const facetForm = document.querySelector('facet-filters-form');
      if (facetForm) {
        facetForm.onSubmitForm(searchParams.toString(), event);
      }
    }
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
     
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        InitCustomFunctions();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    InitCustomFunctions();
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
    
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
    
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const mobileMenuDrawer = document.getElementById('FacetFiltersFormMobile').closest('menu-drawer');
    mobileMenuDrawer.bindEvents();
    
    // Override the onFocusOut behavior for mobile facets
    FacetFiltersForm.initMobileFacetsMenuDrawer(mobileMenuDrawer);
    
    // Re-initialize mobile sort select and close button after content update
    FacetFiltersForm.initMobileSortSelect();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
    InitCustomFunctions();
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }  
    
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
    InitCustomFunctions();
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();


 

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    InitCustomFunctions();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
     InitCustomFunctions();
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);   
  }
  
}

customElements.define('facet-remove', FacetRemove);





